import React from 'react';
import { MapPin, Phone, Clock, Navigation, ExternalLink } from 'lucide-react';
import { foodTruckInfo, schedule } from '../data/mock';

const EmplacementsPage = () => {
  return (
    <main className="pt-20 min-h-screen bg-[#0E0E0E]">
      {/* Hero */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7C948]/10 border border-[#F7C948]/30 text-[#F7C948] text-sm font-medium mb-6">
              <MapPin size={16} />
              Localisation
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-extrabold text-white mb-4">
              Où nous trouver
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Retrouvez Just'Une Dalle à Malafretaz et découvrez nos spécialités street-food
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {/* Main CTA - Address */}
            <div className="bg-gradient-to-br from-[#F7C948]/20 to-[#F7C948]/5 rounded-2xl p-8 border border-[#F7C948]/30 text-center">
              <div className="w-20 h-20 bg-[#F7C948]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <MapPin size={40} className="text-[#F7C948]" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white mb-2">
                Notre adresse
              </h2>
              <p className="text-gray-300 text-xl mb-6">
                {foodTruckInfo.address}
              </p>
              <a
                href="https://maps.google.com/?q=429+Route+de+Bourg+01340+Malafretaz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#F7C948] text-[#0E0E0E] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#FFE17D] transition-all duration-300 hover:shadow-lg hover:shadow-[#F7C948]/25"
              >
                <Navigation size={22} />
                Ouvrir dans Google Maps
                <ExternalLink size={18} />
              </a>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Phone Card */}
              <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-white/10 hover:border-[#F7C948]/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#F7C948]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={28} className="text-[#F7C948]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-bold text-white mb-2">Téléphone</h3>
                    <p className="text-gray-300 text-lg mb-4">{foodTruckInfo.phone}</p>
                    <a
                      href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-2 bg-[#F7C948] text-[#0E0E0E] px-5 py-2.5 rounded-xl font-semibold hover:bg-[#FFE17D] transition-all duration-300"
                    >
                      <Phone size={18} />
                      Appeler
                    </a>
                  </div>
                </div>
              </div>

              {/* Schedule Card */}
              <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-white/10 hover:border-[#F7C948]/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#F7C948]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={28} className="text-[#F7C948]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-bold text-white mb-4">Horaires</h3>
                    <div className="space-y-2">
                      {schedule.map((item) => (
                        <div
                          key={item.day}
                          className={`flex justify-between items-center text-sm ${
                            item.open ? 'text-gray-300' : 'text-gray-500'
                          }`}
                        >
                          <span>{item.day}</span>
                          <span className={item.open ? 'text-[#F7C948] font-medium' : ''}>
                            {item.hours}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Comment nous rejoindre ?
          </h2>
          <div className="bg-[#0E0E0E] rounded-2xl p-8 border border-white/10">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#F7C948]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <span className="font-display text-2xl font-bold text-[#F7C948]">1</span>
                </div>
                <h3 className="font-semibold text-white mb-2">En voiture</h3>
                <p className="text-gray-400 text-sm">
                  Stationnement gratuit disponible sur place
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#F7C948]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <span className="font-display text-2xl font-bold text-[#F7C948]">2</span>
                </div>
                <h3 className="font-semibold text-white mb-2">GPS</h3>
                <p className="text-gray-400 text-sm">
                  429 Route de Bourg, 01340 Malafretaz
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#F7C948]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <span className="font-display text-2xl font-bold text-[#F7C948]">3</span>
                </div>
                <h3 className="font-semibold text-white mb-2">Nous appeler</h3>
                <p className="text-gray-400 text-sm">
                  {foodTruckInfo.phone}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default EmplacementsPage;
